import React from "react";
import { Icon, mergeStyles, mergeStyleSets, Depths, useTheme } from "@fluentui/react";
import { getIconColors } from "../TestResultNode";
import { Utils } from "../../Utils";
import { Helpers } from "./helpers/Helpers";
import { IRcaDaneValidationDnsResponse } from "../../models/IRcaDaneValidationDnsResponse";
import { IRcaDaneValidationDnsSecResult } from "../../models/IRcaDaneValidationDnsSecResult";
import { DnsSecStatus } from "../../models/IRcaDaneValidationDnsSecResult";
import { IRcaDaneValidationMxRecordDetails } from "../../models/IRcaDaneValidationMxRecordDetails";
import { RcaDaneValidationLabelState } from "../../constants/RcaDaneValidationLabelTypes";
import {
  DNSSEC_AND_DANE_TEST,
  DNS_BASELINE_TEST,
  DNSSEC_ONLY_TEST,
  IDaneValidationTestType,
} from "../../constants/RcaDaneValidationTestTypes";
import { DeferredTooltipHost } from "../DeferredTooltipHost";

/**
 * Test Result Lable interface
 */
interface ITestLabel {
  /**
   * Title
   */
  title: string;
  /**
   * Icon for label
   */
  icon: string;
  /**
   * Class Name for label
   */
  className: string;
  /**
   * Tooltip for test label
   */
  toolTip: string;
}

/**
 * The domain result UI
 *
 * @param props - The component properties
 * @returns The markup
 */
const DaneValidationResultTestLabels: React.FC<{
  /**
   * Response that contains records to be rendered
   */
  response: IRcaDaneValidationDnsResponse<IRcaDaneValidationMxRecordDetails>;
  /**
   * Record that will be used to send an email
   */
  record: IRcaDaneValidationMxRecordDetails | null;

  /**
   * DNSSEC result (from vanity domain)
   */
  domainDnsSecResult: IRcaDaneValidationDnsSecResult | null;

  /**
   * Test type choosed by user
   */
  testType: IDaneValidationTestType;
}> = ({ response, record, domainDnsSecResult, testType }) => {
  const theme = useTheme();
  const iconColors = getIconColors(theme);
  const successTextColor = Utils.getReadableTextColor(iconColors.Success);
  const skippedTextColor = Utils.getReadableTextColor(iconColors.Skipped);
  const classNames = mergeStyleSets({
    checkTypeBox: {
      display: "flex",
      height: 29,
      alignItems: "center",
      padding: "0 8px",
      color: successTextColor,
      backgroundColor: iconColors.Success,
      boxShadow: Depths.depth4,
      selectors: {
        [Utils.mobileQuery]: {
          width: "auto",
          marginBottom: 5,
        },
      },
    },
    checkTypeBoxDisabled: {
      backgroundColor: iconColors.Error,
      color: "#010101",
    },
    checkTypeBoxWarning: {
      backgroundColor: iconColors.SuccessWithWarningsMid,
      color: "#010101",
      fontWeight: 500,
    },
    checkTypeBoxSkipped: {
      backgroundColor: iconColors.Skipped,
      color: skippedTextColor,
    },
    checkTypeBoxIcon: {
      marginRight: 6,
      fontSize: 16,
      fontWeight: 900,
    },
    labelWrapper: {
      display: "flex",
      flexWrap: "wrap",
      gap: "8px",
    },
  });

  // if there is no "best match" record, use the response DnsSecStatus instead
  let dnsSecStatus = record === null ? response.DnsSecStatus : record.DnsSecStatus;

  switch (testType) {
    case DNSSEC_AND_DANE_TEST:
    case DNSSEC_ONLY_TEST:
      // For DNSSEC related tests, if vanity domain DnsSecStatus is not success, use it in priority instead of the one from response or record
      dnsSecStatus = domainDnsSecResult === null || domainDnsSecResult.Status === DnsSecStatus.Success ? dnsSecStatus : domainDnsSecResult;
      break;
    default:
      // For other test types, keep the dnsSecStatus as is
      break;
  }

  const labelsModels = Helpers.getLabelsForMxResponse(record, dnsSecStatus);

  const labelToIconMapper = (labelState: RcaDaneValidationLabelState): string => {
    switch (labelState) {
      case RcaDaneValidationLabelState.Failed:
        return "CalculatorMultiply";
      case RcaDaneValidationLabelState.Skipped:
        return "CalculatorSubtract";
      case RcaDaneValidationLabelState.Success:
        return "CheckMark";
      case RcaDaneValidationLabelState.Warning:
        return "Important";
      default:
        return "";
    }
  };

  const labelToClassNameMapper = (labelState: RcaDaneValidationLabelState): string => {
    switch (labelState) {
      case RcaDaneValidationLabelState.Failed:
        return classNames.checkTypeBoxDisabled;
      case RcaDaneValidationLabelState.Skipped:
        return classNames.checkTypeBoxSkipped;
      case RcaDaneValidationLabelState.Success:
        return classNames.checkTypeBox;
      case RcaDaneValidationLabelState.Warning:
        return classNames.checkTypeBoxWarning;
      default:
        return "";
    }
  };
  // create all labels
  const dnsLabel: ITestLabel = {
    title: "DNS",
    toolTip: labelsModels.DNS.tooltip,
    icon: labelToIconMapper(labelsModels.DNS.state),
    className: mergeStyles(classNames.checkTypeBox, labelToClassNameMapper(labelsModels.DNS.state)),
  };
  const dnssecLabel: ITestLabel = {
    title: "DNSSEC",
    toolTip: labelsModels.DNSSEC.tooltip,
    icon: labelToIconMapper(labelsModels.DNSSEC.state),
    className: mergeStyles(classNames.checkTypeBox, labelToClassNameMapper(labelsModels.DNSSEC.state)),
  };
  const tlsaLabel: ITestLabel = {
    title: "TLSA",
    toolTip: labelsModels.TLSA.tooltip,
    icon: labelToIconMapper(labelsModels.TLSA.state),
    className: mergeStyles(classNames.checkTypeBox, labelToClassNameMapper(labelsModels.TLSA.state)),
  };
  const daneLabel: ITestLabel = {
    title: "DANE",
    toolTip: labelsModels.DANE.tooltip,
    icon: labelToIconMapper(labelsModels.DANE.state),
    className: mergeStyles(classNames.checkTypeBox, labelToClassNameMapper(labelsModels.DANE.state)),
  };

  let labels: ITestLabel[] = [];

  switch (testType) {
    case DNS_BASELINE_TEST:
      labels = [dnsLabel];
      break;

    case DNSSEC_AND_DANE_TEST:
      labels = [dnssecLabel, tlsaLabel, daneLabel];
      break;

    case DNSSEC_ONLY_TEST:
      labels = [dnssecLabel];
      break;
  }

  return (
    <div className={classNames.labelWrapper}>
      {labels.map((label) => (
        <DeferredTooltipHost key={label.title} content={label.toolTip}>
          <div className={label.className}>
            <Icon iconName={label.icon} className={classNames.checkTypeBoxIcon} />
            {label.title}
          </div>
        </DeferredTooltipHost>
      ))}
    </div>
  );
};

export default DaneValidationResultTestLabels;
